import * as PropTypes from 'prop-types';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PlateSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SCHEMA_FIELD_SHAPE} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';

export {PlateSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';

export interface StorableSchemaSummary {
  id: string;
  name: string;
  prefix: string;
  registryId: string;
  iconId: BenchlingIconName;
  sqlIdentifier: string;
  archiveRecord?: ArchiveRecord | null;
}

const STORABLE_SCHEMA_SUMMARY_PROPS = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  registryId: PropTypes.string,
};

export type StorableSchema = Schema & StorableSchemaSummary;

export interface GridSchema extends StorableSchema {
  containerSchemaId: string | null;
  height: number;
  plateSchemaType?: string;
  width: number;
}

export function isGridSchema(storableSchema: StorableSchema): storableSchema is GridSchema {
  return [ModelTypeConstants.PLATE_SCHEMA, ModelTypeConstants.BOX_SCHEMA].includes(
    APIUtil.apiIdToType(storableSchema.id)
  );
}

const STORABLE_SCHEMA_PROPS = {
  ...STORABLE_SCHEMA_SUMMARY_PROPS,
  fields: PropTypes.arrayOf(SCHEMA_FIELD_SHAPE),
  sqlIdentifier: PropTypes.string,
};

export const STORABLE_SCHEMA_SHAPE = PropTypes.shape(STORABLE_SCHEMA_PROPS);

export type ContainerSchema = StorableSchema;

export const PLATE_SCHEMA_TYPE = {
  FIXED_PLATE: PlateSchemaType.FIXED_PLATE,
  MATRIX_PLATE: PlateSchemaType.MATRIX_PLATE,
};

export const PLATE_SCHEMA_TYPE_TO_TEXT: {[plateSchemaType: string]: string} = {
  FIXED_PLATE: 'Well plate',
  MATRIX_PLATE: 'Matrix plate',
};

export type BoxSchema = GridSchema;
export interface PlateSchema extends GridSchema {
  plateSchemaType: PlateSchemaType;
}

export type LocationSchema = StorableSchema;

export type PlateSize = [rows: number, columns: number];

// Keep in sync with PREEXISTING_PLATE_SCHEMA_SIZE_OPTIONS in benchling/configs/entrypoint/register_validations.py
export const PLATE_SCHEMA_SIZE_OPTIONS = [
  [16, 24],
  [8, 12],
  [6, 8],
  [4, 6],
  [3, 4],
  [2, 3],
] satisfies Array<PlateSize>;

export enum ParentStorableType {
  GRID = 'GRID',
  LOCATION = 'LOCATION',
  GRID_OR_LOCATION = 'GRID_OR_LOCATION',
  PLATE = 'PLATE',
  MATRIX_PLATE = 'MATRIX_PLATE',
  BOX = 'BOX',
}
